import { theme } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { mapActions } from 'reduxStore/maps/slice';
import { UploadBox } from 'zComponents/atoms';
import { Button, Checkbox, Col, Drawer, Form, Input, Row, Space, Typography } from 'zui/Antd';

const { Text, Title } = Typography;

function UpdateFloorPlanDrawer({ open = false, setOpen, floorInfo }) {
	const [form] = Form.useForm();
	const { url: photoUrl, floorID, orgID } = floorInfo || {};
	const workspacesData = useSelector((state) => state.Organisations.data);

	const orgName = workspacesData?.find((org) => org.orgID === orgID)?.name ?? '';

	const uploadRef = useRef(null);
	const [imagePath, setImagePath] = useState(photoUrl);
	const [image, setImage] = useState(photoUrl);
	useEffect(() => {
		setImagePath(photoUrl);
		setImage(photoUrl);
	}, [photoUrl]);

	const {
		token: {
			paddingXXS,
			paddingLG,
			paddingXXL,
			paddingXL,
			colorText,
			colorTextLabel,
			padding,
			colorTextDescription
		}
	} = theme.useToken();

	const handleCancel = () => {
		form.resetFields();
		setOpen(false);
	};

	const dispatch = useDispatch();

	const handleFinish = (values) => {
		const mapBody = {
			name: values.mapName,
			// xDimension: values.xDimension,
			// yDimension: values.yDimension,
			// notes: values.notes,
			entityID: floorID.toString(),
			entityTypeID: '3',
			url: imagePath
		};

		dispatch(
			mapActions.updateMap({
				mapBody,
				entityName: 'Floor',
				entityID: floorID,
				uploadType: 'indoorMap',
				oldData: { mapID: floorInfo.mapID, url: photoUrl }
			})
		);
		handleCancel();
	};

	const labelStyles = {
		fontSize: 14,
		color: colorTextLabel,
		fontWeight: 600
	};
	useEffect(() => {
		if (image) {
			form.setFieldsValue({ url: imagePath });
			form.validateFields(['url']).catch(() => {});
		}
	}, [image, form, imagePath]);
	return (
		<Drawer
			placement='right'
			width='30%'
			onClose={() => setOpen(false)}
			open={open}
			destroyOnClose
			bodyStyle={{ paddingBlock: paddingLG, paddingInline: paddingXXL }}
		>
			<Space direction='vertical' style={{ width: '100%' }} size={paddingXL}>
				<Space direction='vertical' size={paddingXXS}>
					<Title level={4} style={{ margin: 0, color: colorText }}>
						Update Floor Plan
					</Title>
					<Text style={{ color: colorTextLabel, fontSize: '12px' }}>
						Upload a new floor plan or modify the existing one.
					</Text>
				</Space>

				<Form form={form} layout='vertical' onFinish={handleFinish} requiredMark={false}>
					<Form.Item>
						<Space size={paddingXXS} direction='vertical' style={{ width: '100%' }}>
							<Text style={labelStyles}>Location</Text>
							<Text style={{ fontSize: '12px', color: colorTextLabel }}>
								{orgName}, {floorInfo?.siteName}
							</Text>
						</Space>
					</Form.Item>
					<Space size={paddingXXS} direction='vertical' style={{ width: '100%' }}>
						<Form.Item
							name='mapName'
							label={<Text style={labelStyles}>Map Name</Text>}
							rules={[{ required: true, message: 'Please enter a map name' }]}
						>
							<Input placeholder='eg. Main Office floor 1' />
						</Form.Item>
					</Space>
					<Row gutter={16}>
						<Col span={12}>
							<Form.Item
								name='xDimension'
								label={<Text style={labelStyles}>X Dimension</Text>}
								rules={[{ required: false }]}
							>
								<Input placeholder='eg. 20' />
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item
								name='yDimension'
								label={<Text style={labelStyles}>Y Dimension</Text>}
								rules={[{ required: false }]}
							>
								<Input placeholder='eg. 24.5' />
							</Form.Item>
						</Col>
					</Row>
					<Space size={paddingXXS} direction='vertical' style={{ width: '100%' }}>
						<Form.Item
							labelCol={{ Text: 5 }}
							name='notes'
							label={<Text style={labelStyles}>Notes</Text>}
							rules={[{ required: false }]}
						>
							<TextArea
								style={{ color: colorTextDescription }}
								allowClear
								autoSize={{ minRows: 4, maxRows: 6 }}
								placeholder='Add any additional information about this floor plan'
							/>
						</Form.Item>
					</Space>

					<Space direction='vertical' size={paddingXXS} style={{ width: '100%', marginBottom: paddingXL }}>
						<Space size={paddingXXS} direction='vertical' style={{ width: '100%' }}>
							<Text style={labelStyles}>Upload Floor Plan</Text>
							<Form.Item
								name='url'
								style={{ width: '100%' }}
								rules={[
									{
										validator: () =>
											image ? Promise.resolve() : Promise.reject(new Error('Please select image'))
									}
								]}
							>
								<UploadBox
									entityID={3}
									photoUrl={photoUrl || null}
									size={150}
									entityName={'Floor'}
									shape={'square'}
									cursor={true}
									edit={true}
									uploadRef={uploadRef}
									setImage={setImage}
									image={image}
									uploadType={'indoorMap'}
									setImagePath={setImagePath}
									imagePath={imagePath}
									mapData={{
										name: 'test name',
										entityID: floorID,
										entityTypeID: 3
									}}
								/>
							</Form.Item>
						</Space>

						<Form.Item
							name='confirmDisassociate'
							valuePropName='checked'
							style={{ margin: 0 }}
							rules={[
								{
									type: 'boolean', // ← validate as boolean
									required: true,
									message: 'Please confirm'
								}
							]}
							validateTrigger={['onChange', 'onBlur']}
						>
							<Checkbox>
								<Text style={{ color: colorText, fontSize: '14px', lineHeight: '1.5' }}>
									I understand that uploading a new floor plan will disassociate all devices currently
									associated with this map.
								</Text>
							</Checkbox>
						</Form.Item>
					</Space>

					<Space direction='vertical' size={padding} style={{ width: '100%' }}>
						<Form.Item style={{ marginBottom: 0 }}>
							<Button type='primary' htmlType='submit' block>
								Save Changes
							</Button>
						</Form.Item>
						<Button onClick={handleCancel} block>
							Cancel
						</Button>
					</Space>
				</Form>
			</Space>
		</Drawer>
	);
}

export default UpdateFloorPlanDrawer;
